import React, { useEffect, useMemo, useState } from "react";

import { Loader2 } from "lucide-react";

import {
  ASSESSMENT_LEVEL_RL,
  buildRlLevelLookupMaps,
} from "./organizationLevelUtils";
import { HistoryEntryCard } from "./rl-assessment-history-panel";
import Button from "../../../Components/Button";
import CommonModal from "../../../Components/Modal";
import Select from "../../../Components/Select";
import { useAppDispatch } from "../../../Store/Hooks";
import {
  fetchRelationshipAssessmentHistory,
  fetchRelationshipAssessmentLevels,
} from "../../../Store/Reducers/RelationshipSlice";

function successorOptionsHtml(
  candidates,
  contactId,
  { excludeCollisions = true } = {}
) {
  const filtered = (candidates || []).filter(c => {
    if (!excludeCollisions || !contactId) return true;
    return !(c.active_contact_ids || []).some(
      id => String(id) === String(contactId)
    );
  });
  const recommended = filtered.filter(c => c.is_same_role);
  const others = filtered.filter(c => !c.is_same_role);
  const option = c =>
    `${c.full_name || c.user_id} — ${c.coverage_role || c.role_name || "Team"}`;
  return { filtered, recommended, others, option };
}

function toSelectOptions(list, option) {
  return (list || []).map(c => ({
    value: String(c.user_id),
    label: option(c),
  }));
}

export function DormantHoleModal({
  open,
  onClose,
  person,
  hole,
  accountId = null,
  candidates = [],
  historyOnly = false,
  nested = false,
  onTransfer,
  onDismiss,
}) {
  const dispatch = useAppDispatch();
  const [successorId, setSuccessorId] = useState("");
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [rlLevelNames, setRlLevelNames] = useState({});

  const ownerUserId = person?.user_id || person?.owner_user_id || null;
  const contactId = hole?.contact_id || null;

  const { filtered, recommended, others, option } = useMemo(
    () => successorOptionsHtml(candidates, hole?.contact_id),
    [candidates, hole?.contact_id]
  );
  const excludedCount = (candidates?.length || 0) - filtered.length;
  const canTransfer = !historyOnly && filtered.length > 0;

  const selectGroups = useMemo(() => {
    const groups = [];
    if (recommended.length) {
      groups.push({
        label: "Recommended — same role",
        options: toSelectOptions(recommended, option),
      });
    }
    if (others.length) {
      groups.push({
        label: "Other matrix owners",
        options: toSelectOptions(others, option),
      });
    }
    return groups;
  }, [recommended, others, option]);

  useEffect(() => {
    if (!open) setSuccessorId("");
  }, [open, hole?.relationship_id]);

  useEffect(() => {
    let cancelled = false;
    dispatch(fetchRelationshipAssessmentLevels(ASSESSMENT_LEVEL_RL))
      .unwrap()
      .then(levels => {
        if (cancelled) return;
        const { namesByLevel } = buildRlLevelLookupMaps(levels);
        setRlLevelNames(namesByLevel);
      })
      .catch(() => {
        /* optional labels */
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!open || !accountId || !ownerUserId || !contactId) {
        setAssessments([]);
        setAssessmentsLoading(false);
        return;
      }
      setAssessmentsLoading(true);
      try {
        const rows = await dispatch(
          fetchRelationshipAssessmentHistory({
            accountId: String(accountId),
            userId: String(ownerUserId),
            contactId: String(contactId),
          })
        ).unwrap();
        if (!cancelled) setAssessments(rows || []);
      } catch {
        if (!cancelled) setAssessments([]);
      } finally {
        if (!cancelled) setAssessmentsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    accountId,
    ownerUserId,
    contactId,
    dispatch,
    hole?.relationship_id,
  ]);

  let goalFallbackLabel = "No goal set";
  if (hole?.goal_target_rl != null) {
    goalFallbackLabel = `Target RL ${hole.goal_target_rl}`;
  } else if (hole?.has_goal) {
    goalFallbackLabel = "Goal was set";
  }

  const footer = historyOnly ? (
    <Button variant="primary" onClick={onClose}>
      Close
    </Button>
  ) : (
    <div className="dormant-hole-footer">
      {excludedCount > 0 ? (
        <p className="dormant-hole-excluded">
          {excludedCount} matrix owner{excludedCount !== 1 ? "s" : ""} not
          listed — already cover{excludedCount === 1 ? "s" : ""}{" "}
          {hole?.contact_name || "this contact"}.
        </p>
      ) : null}
      <div className="dormant-hole-actions">
        <div className="dormant-hole-actions-secondary">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => onDismiss?.(hole)}>
            Dismiss
          </Button>
        </div>
        {canTransfer ? (
          <Button
            variant="primary"
            disabled={!successorId}
            onClick={() => {
              if (!successorId) return;
              onTransfer?.({ hole, successorId, person });
            }}
          >
            Transfer
          </Button>
        ) : null}
      </div>
    </div>
  );

  return (
    <CommonModal
      isOpen={open}
      toggle={onClose}
      size="lg"
      nested={nested}
      className="rl-prototype-skin"
      title={
        person && hole
          ? `${person.full_name} → ${hole.contact_name || "Contact"}`
          : "Dormant relationship"
      }
      footer={footer}
    >
      <div className="dormant-hole-modal">
        <div className="dormant-hole-badge">
          {hole?.orphan_tier === 3 ? (
            <span className="dormant-hole-badge--urgent">
              Orphaned — no active owner
            </span>
          ) : (
            <span className="dormant-hole-badge--muted">
              Covered by another active owner
            </span>
          )}
        </div>

        <p className="dormant-hole-note">
          History is preserved. This record is view-only — RL and goals cannot
          be updated here.
        </p>

        <div className="dormant-hole-snapshot">
          <div className="dormant-hole-snapshot-hdr">
            Assessment & goal at departure
          </div>
          {assessmentsLoading ? (
            <div className="dormant-hole-snapshot-loading">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading assessment details…
            </div>
          ) : null}
          {!assessmentsLoading && assessments.length === 0 ? (
            <div className="dormant-hole-snapshot-empty">
              <p>
                <strong>Last assessment:</strong>{" "}
                {hole?.latest_rl != null
                  ? `RL ${hole.latest_rl}`
                  : "Never assessed"}
              </p>
              <p>
                <strong>Goal at departure:</strong> {goalFallbackLabel}
              </p>
              <p className="dormant-hole-snapshot-empty-hint">
                No posture or rationale was captured for this pair.
              </p>
            </div>
          ) : null}
          {!assessmentsLoading && assessments.length > 0 ? (
            <ul className="dormant-hole-history-list">
              {assessments.map(entry => (
                <HistoryEntryCard
                  key={entry.id}
                  entry={entry}
                  rlLevelNames={rlLevelNames}
                  currentLabel={historyOnly ? "At departure" : "Latest"}
                />
              ))}
            </ul>
          ) : null}
        </div>

        {canTransfer ? (
          <div className="dormant-hole-transfer-field">
            <Select
              name="dormant-hole-successor"
              label="Transfer to"
              className="mb-0"
              value={successorId}
              placeholder="Select a matrix owner…"
              groups={selectGroups}
              onChange={e => setSuccessorId(e.target.value || "")}
            />
          </div>
        ) : null}
      </div>
    </CommonModal>
  );
}

export function BulkTransferModal({
  open,
  onClose,
  person,
  successor,
  holes = [],
  onConfirm,
}) {
  const [checked, setChecked] = React.useState({});

  React.useEffect(() => {
    if (!open || !successor || !holes.length) return;
    const next = {};
    holes.forEach(h => {
      const collides = (successor.active_contact_ids || []).some(
        id => String(id) === String(h.contact_id)
      );
      next[h.relationship_id] = !collides;
    });
    setChecked(next);
  }, [open, successor, holes]);

  if (!open || !person || !successor) return null;

  const selectedCount = holes.filter(h => checked[h.relationship_id]).length;

  return (
    <CommonModal
      isOpen={open}
      toggle={onClose}
      size="lg"
      className="rl-prototype-skin"
      title={`Transfer to ${successor.full_name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!selectedCount}
            onClick={() => {
              const transfers = holes
                .filter(h => checked[h.relationship_id])
                .map(h => ({
                  relationship_id: h.relationship_id,
                  successor_user_id: successor.user_id,
                }));
              onConfirm?.(transfers);
            }}
          >
            Confirm transfer
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-gray-600">
        Transfer selected relationships from {person.full_name} to{" "}
        {successor.full_name}. Each one&apos;s history stays preserved.
      </p>
      <div className="bulk-transfer-list">
        {holes.map(hole => {
          const collides = (successor.active_contact_ids || []).some(
            id => String(id) === String(hole.contact_id)
          );
          return (
            <label
              key={hole.relationship_id}
              className={`bulk-transfer-row${collides ? " is-disabled" : ""}`}
            >
              <input
                type="checkbox"
                disabled={collides}
                checked={Boolean(checked[hole.relationship_id])}
                onChange={e =>
                  setChecked(prev => ({
                    ...prev,
                    [hole.relationship_id]: e.target.checked,
                  }))
                }
              />
              <span className="bulk-transfer-name">
                {hole.contact_name || hole.contact_id}
              </span>
              <span
                className="bulk-transfer-meta"
                title={
                  !collides && hole.latest_rl == null ? "Unassessed" : undefined
                }
              >
                {collides
                  ? `${successor.full_name} already covers this`
                  : hole.latest_rl != null
                    ? `RL ${hole.latest_rl}`
                    : "RL ?"}
              </span>
            </label>
          );
        })}
      </div>
    </CommonModal>
  );
}

export { successorOptionsHtml };
